import React, { useState, useEffect } from 'react';

interface Image {
  url: string;
  alt: string;
}

interface HotelImageGalleryProps {
  images: Image[];
  featuredImage: Image;
  setFeaturedImage: (image: Image) => void;
}

const HotelImageGallery: React.FC<HotelImageGalleryProps> = ({ images, featuredImage, setFeaturedImage }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState<{ [key: string]: HTMLImageElement }>({});
  //const [showAllThumbnails, setShowAllThumbnails] = useState(false); // State to toggle full thumbnails

  const maxThumbnails = 7; // Show max 6 thumbnails
  const remainingCount = images.length - maxThumbnails;

  const handleNext = () => {
    const nextIndex = (currentIndex + 1) % images.length;
    setCurrentIndex(nextIndex);
    setFeaturedImage(images[nextIndex]);
    setIsLoaded(false);
  };

  const handlePrev = () => {
    const prevIndex = (currentIndex - 1 + images.length) % images.length;
    setCurrentIndex(prevIndex);
    setFeaturedImage(images[prevIndex]);
    setIsLoaded(false);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      handleNext();
    }, 3000);

    return () => clearInterval(interval);
  }, [currentIndex]);

  useEffect(() => {
    const preloadImages = () => {
      const loadedImages: { [key: string]: HTMLImageElement } = {};
      images.forEach((image) => {
        const img = new Image();
        img.src = image.url;
        img.onload = () => {
          loadedImages[image.url] = img;
          if (Object.keys(loadedImages).length === images.length) {
            setPreloadedImages(loadedImages);
          }
        };
      });
    };

    preloadImages();
  }, [images]);

  useEffect(() => {
    if (preloadedImages[featuredImage.url]) {
      setIsLoaded(true);
    }
  }, [featuredImage, preloadedImages]);

  return (
    <div className="flex flex-col">
      <div className="relative">
        {/* Main Featured Image */}
        <img
          src={featuredImage.url}
          alt={featuredImage.alt}
          className={`hidden md:block lg:block md:w-full md:h-[450px] lg:w-[1024px] lg:h-[450px] lg:object-cover lg:object-center transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0 blur-lg'}`}
          loading="lazy"
        />

        {/* Navigation Arrows */}
        <button
          onClick={handlePrev}
          className="hidden md:block absolute left-2 top-1/2 transform -translate-y-1/2 bg-gray-800 bg-opacity-50 text-white w-12 h-12 rounded-full focus:outline-none hover:bg-opacity-75 hover:scale-110 transition duration-200 ease-in-out">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6 mx-auto">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={handleNext}
          className="hidden md:block absolute right-2 top-1/2 transform -translate-y-1/2 bg-gray-800 bg-opacity-50 text-white w-12 h-12 rounded-full focus:outline-none hover:bg-opacity-75 hover:scale-110 transition duration-200 ease-in-out">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6 mx-auto">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Mobile Image Slider */}
        <div className="md:hidden lg:hidden relative w-full h-[250px]">
          <img
            src={images[currentIndex].url}
            alt={`Mobile Image ${currentIndex + 1}`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Thumbnails Section */}
      <div className="mt-2">
        <div className="hidden md:flex md:space-x-2 lg:flex lg:space-x-2">
          {(images.slice(0, maxThumbnails)).map((image, index) => (
            <button
              key={index}
              className="focus:outline-none"
              onClick={() => {
                setFeaturedImage(image);
                setCurrentIndex(index);
              }}
              aria-label={`Hotel Image ${index + 1}`}>
              <img
                src={image.url}
                alt={`Thumbnail ${index + 1}`}
                className="rounded-md object-cover object-center w-32 h-20 sm:w-24 sm:h-24"
                loading="lazy"
              />
            </button>
          ))}

          {/* Show "+X more" Button if there are more than 6 images */}
          {images.length > maxThumbnails && (
            <button
              //onClick={() => setShowAllThumbnails(true)}
              className="relative w-32 h-20 sm:w-24 sm:h-24 bg-gray-300 rounded-md flex justify-center items-center text-white font-bold text-lg">
              +{remainingCount} more
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default HotelImageGallery;
